import React, { useState, useEffect } from "react";
import MetricCard from "../../components/MetricCard/MetricCard";
import WaterPressureCard from "../../components/WaterPressureCard/WaterPressureCard";
import WaterAreaChart from "../../components/WaterAreaChart/WaterAreaChart";
import WaterBarChart from "../../components/WaterBarChart/WaterBarChart";
import MonthlyComparisonChart from "../../components/MonthlyComparisonChart/MonthlyComparisonChart";
import FilterPanel from "../../components/FilterPanel/FilterPanel";
import { getConsumptionStatus } from "../../utils/waterConsumption";
import { useUserConfig } from "../../context/UserConfigContext";
import { useUnit } from "../../context/UnitContext";
import { calculateHousingBillByLiters, getUnitSymbol } from "../../utils/unitConversion";
// Pasa por el repositorio en lugar de api.js: así la vista de dispositivo y la
// de cuenta leen de la misma fuente y muestran cifras coherentes entre sí.
import telemetryRepository from "../../services/repositories/telemetryRepository";
import { isDemoSession } from "../../services/demoSession";
import { useDevices } from "../../context/DeviceContext";
import AccountOverview from "../../components/AccountOverview/AccountOverview";
import {
  deriveStatus,
  DEVICE_STATUS,
  STATUS_LABEL,
  formatLastSeen,
} from "../../domain/device";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from "recharts";
import WaterDropRoundedIcon from "@mui/icons-material/WaterDropRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import RequestQuoteRoundedIcon from "@mui/icons-material/RequestQuoteRounded";

import useChartTokens from "../../theme/chartTokens";
import "./Home.css";

/* Adaptadores al repositorio: conservan las firmas que ya usaba esta pantalla,
   de modo que el resto del archivo no cambia. */
const obtenerMediciones = (deviceId) => telemetryRepository.getCurrentReading(deviceId);
const getDeviceTelemetry = (deviceId, limit, hours) =>
  telemetryRepository.getTelemetry(deviceId, { limit, hours });
const getAggregatedTelemetry = (deviceId, opciones) =>
  telemetryRepository.getAggregated(deviceId, opciones);

const getDemoConsumptionStatus = (dailyConsumption, householdMembers, configuredGoal) => {
  const limit = Number(configuredGoal);
  if (!(limit > 0)) return getConsumptionStatus(dailyConsumption, householdMembers);
  const percentage = (dailyConsumption / limit) * 100;
  if (percentage >= 100) return { status: 'error', message: '¡Consumo excesivo!', percentage };
  if (percentage >= 80) return { status: 'warning', message: 'Consumo elevado', percentage };
  return { status: 'success', message: 'Consumo óptimo', percentage };
};

const PressureTooltip = ({ active, payload, label, color }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="ng-chart-tooltip">
      <p className="ng-chart-tooltip__label">{label}</p>
      <p className="ng-chart-tooltip__value">
        <span className="ng-chart-tooltip__dot" style={{ background: color }} />
        Presión: <strong>{Number(payload[0].value).toFixed(1)} psi</strong>
      </p>
    </div>
  );
};

function Home() {
  const [currentFlow, setCurrentFlow] = useState(0);
  const [todayConsumption, setTodayConsumption] = useState(0);
  const [weekConsumption, setWeekConsumption] = useState(0);
  const [monthConsumption, setMonthConsumption] = useState(0);
  const [selectedTimePeriod, setSelectedTimePeriod] = useState("day");
  /* `esp32Connected` indica si la ÚLTIMA CONSULTA devolvió datos, que no es lo
     mismo que el dispositivo esté conectado. Sirve para saber si hay lectura
     fresca en pantalla; el estado del equipo lo da `estadoDispositivo`, que
     deriva del `last_seen` registrado en AWS. */
  const [esp32Connected, setEsp32Connected] = useState(false);
  const [waterPressure, setWaterPressure] = useState(0);
  const [pressureHistory, setPressureHistory] = useState([]);

  const { householdMembers, dailyGoal } = useUserConfig();

  const { formatValueWithUnit, selectedUnit } = useUnit();
  const { selectedDevice, isAccountScope } = useDevices();
  const demoValveState = isDemoSession() ? selectedDevice?.valve_state : null;
  const chart = useChartTokens();

  /* Estado real del dispositivo, derivado del último reporte registrado en AWS.
     Se recalcula en cada render para que el paso de "en línea" a "desconectado"
     ocurra solo con el tiempo, sin esperar a una petición nueva. */
  const estadoDispositivo = deriveStatus(selectedDevice);

  const [areaChartData, setAreaChartData] = useState([
    { hour: "00:00", consumption: 0 },
    { hour: "01:00", consumption: 0 },
    { hour: "02:00", consumption: 0 },
    { hour: "03:00", consumption: 0 },
    { hour: "04:00", consumption: 0 },
  ]);

  const [dailyFullChartData, setDailyFullChartData] = useState([]);
  const [weeklyChartData, setWeeklyChartData] = useState([]);

  useEffect(() => {
    const now = new Date();
    const currentHour = now.getHours();

    const last5HoursData = [];
    for (let i = 4; i >= 0; i--) {
      const hour = ((currentHour - i + 24) % 24).toString().padStart(2, "0") + ":00";
      last5HoursData.push({ hour, consumption: 0 });
    }

    const fullDailyData = [];
    for (let i = 0; i < 24; i++) {
      const hour = i.toString().padStart(2, "0") + ":00";
      fullDailyData.push({ hour, consumption: 0 });
    }

    const weeklyData = [
      { day: "Lun", consumo: 0 },
      { day: "Mar", consumo: 0 },
      { day: "Mié", consumo: 0 },
      { day: "Jue", consumo: 0 },
      { day: "Vie", consumo: 0 },
      { day: "Sáb", consumo: 0 },
      { day: "Dom", consumo: 0 },
    ];

    setAreaChartData(last5HoursData);
    setDailyFullChartData(fullDailyData);
    setWeeklyChartData(weeklyData);

    setTodayConsumption(0);
    setWeekConsumption(0);
    setMonthConsumption(0);
  }, []);

  const [monthlyComparison, setMonthlyComparison] = useState({
    currentMonth: 0,
    previousMonth: 0,
    twoMonthsAgo: 0,
    savingsPercentage: 0,
    savingsAmount: 0,
  });

  useEffect(() => {
    const calculateSavings = () => {
      const previousBill = calculateHousingBillByLiters(
        monthlyComparison.previousMonth,
        "governmental"
      );
      const currentBill = calculateHousingBillByLiters(
        monthlyComparison.currentMonth,
        "governmental"
      );

      const savingsAmount = previousBill.total - currentBill.total;
      const savingsPercentage =
        monthlyComparison.previousMonth > 0
          ? ((monthlyComparison.previousMonth - monthlyComparison.currentMonth) /
              monthlyComparison.previousMonth) *
            100
          : 0;

      setMonthlyComparison((prev) => ({
        ...prev,
        savingsPercentage: Math.round(savingsPercentage),
        savingsAmount: Math.round(savingsAmount),
      }));
    };

    calculateSavings();
  }, [monthlyComparison.currentMonth, monthlyComparison.previousMonth]);

  useEffect(() => {
    if (!isDemoSession()) return;
    setCurrentFlow(0);
    setWaterPressure(0);
    setPressureHistory([]);
    setTodayConsumption(0);
    setWeekConsumption(0);
    setMonthConsumption(0);
    setAreaChartData([]);
    setDailyFullChartData([]);
    setWeeklyChartData([]);
    setMonthlyComparison((prev) => ({ ...prev, currentMonth: 0, previousMonth: 0, twoMonthsAgo: 0 }));
  }, [selectedDevice?.device_id]);

  useEffect(() => {
    const fetchWeeklyData = async () => {
      if (!selectedDevice?.device_id) return;

      try {
        const response = await getAggregatedTelemetry(selectedDevice.device_id, 7);

        if (response && response.dailyData && response.dailyData.length > 0) {
          const dayNames = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

          const chartData = response.dailyData.map((day) => {
            const date = new Date(day.date + "T12:00:00");
            const dayName = dayNames[date.getDay()];

            return {
              day: dayName,
              consumo: day.consumption,
              date: day.date,
            };
          });

          chartData.sort((a, b) => new Date(a.date) - new Date(b.date));
          setWeeklyChartData(chartData);

          // Los totales salen de aqui y no de la ultima muestra de telemetria:
          // el dispositivo nunca envia daily_consumption (manda daily_usage, que
          // ademas es el acumulado desde el arranque), asi que ese campo llega
          // siempre en 0. dailyData si trae el consumo real, integrado por hora.
          // Misma referencia que el backend al agrupar por dia: UTC-6 fijo
          // (Mexico Central, sin horario de verano desde 2022). Usar la zona del
          // navegador daria el dia equivocado a quien mire desde otro huso.
          const mexico = new Date(Date.now() - 6 * 60 * 60 * 1000);
          const local = new Date();
          const claveHoy = isDemoSession()
            ? `${local.getFullYear()}-${String(local.getMonth() + 1).padStart(2, '0')}-${String(local.getDate()).padStart(2, '0')}`
            : `${mexico.getUTCFullYear()}-${String(mexico.getUTCMonth() + 1).padStart(2, "0")}-${String(mexico.getUTCDate()).padStart(2, "0")}`;
          const filaHoy = response.dailyData.find((d) => d.date === claveHoy);

          setTodayConsumption(filaHoy ? filaHoy.consumption : 0);
          setWeekConsumption(
            response.dailyData.reduce((total, d) => total + d.consumption, 0)
          );
        }
      } catch (error) {
        console.error("Error cargando datos semanales:", error);
      }
    };

    fetchWeeklyData();
    const interval = setInterval(fetchWeeklyData, isDemoSession() ? 5000 : 600000);
    return () => clearInterval(interval);
  }, [selectedDevice?.device_id, demoValveState]);

  useEffect(() => {
    const fetchMonthlyComparison = async () => {
      if (!selectedDevice?.device_id) return;

      try {
        const response = await getAggregatedTelemetry(selectedDevice.device_id, 90);

        if (response && response.dailyData && response.dailyData.length > 0) {
          const now = new Date();
          const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
          const previousMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          const twoMonthsAgoStart = new Date(now.getFullYear(), now.getMonth() - 2, 1);

          let currentMonthTotal = 0;
          let previousMonthTotal = 0;
          let twoMonthsAgoTotal = 0;

          response.dailyData.forEach((day) => {
            const dayDate = new Date(day.date + "T12:00:00");

            if (dayDate >= currentMonthStart) {
              currentMonthTotal += day.consumption;
            } else if (dayDate >= previousMonthStart && dayDate < currentMonthStart) {
              previousMonthTotal += day.consumption;
            } else if (dayDate >= twoMonthsAgoStart && dayDate < previousMonthStart) {
              twoMonthsAgoTotal += day.consumption;
            }
          });

          setMonthConsumption(currentMonthTotal);

          setMonthlyComparison((prev) => ({
            ...prev,
            currentMonth: currentMonthTotal,
            previousMonth: previousMonthTotal,
            twoMonthsAgo: twoMonthsAgoTotal,
          }));
        }
      } catch (error) {
        console.error("Error cargando comparación mensual:", error);
      }
    };

    fetchMonthlyComparison();
    const interval = setInterval(fetchMonthlyComparison, isDemoSession() ? 30000 : 3600000);
    return () => clearInterval(interval);
  }, [selectedDevice?.device_id, demoValveState]);

  // En la demo, ambas gráficas horarias parten del mismo agregado que usan
  // Historial y los totales diarios. La ruta real conserva su telemetría cruda.
  useEffect(() => {
    if (!isDemoSession() || !selectedDevice?.device_id) return undefined;
    let cancelled = false;
    const fetchDemoHours = async () => {
      try {
        const { dailyData } = await getAggregatedTelemetry(selectedDevice.device_id, 2);
        if (cancelled) return;
        const porHora = new Map(dailyData.flatMap((day) =>
          (day.hourlyBreakdown ?? []).map((hour) => [`${day.date}|${hour.hour}`, hour.consumption])
        ));
        const now = new Date();
        const ultimas24 = Array.from({ length: 24 }, (_, index) => {
          const date = new Date(now);
          date.setHours(now.getHours() - (23 - index), 0, 0, 0);
          const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}|${date.getHours()}`;
          return {
            hour: `${String(date.getHours()).padStart(2, '0')}:00`,
            consumption: Number(porHora.get(key) ?? 0),
          };
        });
        setDailyFullChartData(ultimas24);
        setAreaChartData(ultimas24.slice(-5).map((point, index) => ({
          ...point,
          consumption: +(
            point.consumption / (index === 4 ? Math.max(1, now.getMinutes() + now.getSeconds() / 60) : 60)
          ).toFixed(2),
        })));
      } catch (error) {
        console.error('Error cargando horas simuladas:', error);
      }
    };
    fetchDemoHours();
    const interval = setInterval(fetchDemoHours, 5000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [selectedDevice?.device_id, demoValveState]);

  useEffect(() => {
    if (isDemoSession()) return undefined;
    const fetchLast5HoursAvgFlow = async () => {
      if (!selectedDevice?.device_id) return;

      try {
        const response = await getDeviceTelemetry(selectedDevice.device_id, 1000, 6);

        if (response && response.data && response.data.length > 0) {
          const now = new Date();
          const currentHour = now.getHours();
          const hourlyConsumption = {};

          const sortedData = [...response.data].sort((a, b) => a.timestamp - b.timestamp);

          sortedData.forEach((point) => {
            const date = new Date(point.timestamp * 1000);
            const hour = date.getHours();
            const hourKey = `${hour.toString().padStart(2, "0")}:00`;

            if (!hourlyConsumption[hourKey]) {
              hourlyConsumption[hourKey] = {
                hour: hourKey,
                flowRate: 0,
                count: 0,
              };
            }

            const flowRate = point.flow_rate_lpm || 0;
            hourlyConsumption[hourKey].flowRate += flowRate;
            hourlyConsumption[hourKey].count += 1;
          });

          const last5Hours = [];

          // i=5..1 dejaba fuera la hora en curso: la grafica decia "ultimas 5
          // horas" y mostraba de -5 a -1. Justo la hora con agua no salia nunca.
          // El inicializador de arriba ya usaba 4..0; esto lo alinea.
          for (let i = 4; i >= 0; i--) {
            const hour = (currentHour - i + 24) % 24;
            const hourKey = `${hour.toString().padStart(2, "0")}:00`;
            const hourData = hourlyConsumption[hourKey];

            const avgFlowRate =
              hourData && hourData.count > 0 ? hourData.flowRate / hourData.count : 0;

            last5Hours.push({
              hour: hourKey,
              consumption: Number(avgFlowRate.toFixed(2)),
            });
          }

          setAreaChartData(last5Hours);
        }
      } catch (error) {
        console.error("Error obteniendo flujo promedio de últimas 5 horas:", error);
      }
    };

    fetchLast5HoursAvgFlow();
  }, [selectedDevice?.device_id]);

  useEffect(() => {
    if (isDemoSession()) return undefined;
    const fetchLast24HoursConsumption = async () => {
      if (!selectedDevice?.device_id) return;

      try {
        const response = await getDeviceTelemetry(selectedDevice.device_id, 10000, 25);

        if (response && response.data && response.data.length > 0) {
          const hourlyConsumption = {};

          const sortedData = [...response.data].sort((a, b) => a.timestamp - b.timestamp);

          sortedData.forEach((point) => {
            const date = new Date(point.timestamp * 1000);
            const hour = date.getHours();
            const hourKey = `${hour.toString().padStart(2, "0")}:00`;

            if (!hourlyConsumption[hourKey]) {
              hourlyConsumption[hourKey] = {
                hour: hourKey,
                consumption: 0,
                lastTimestamp: null,
              };
            }

            const flowRate = point.flow_rate_lpm || 0;

            if (hourlyConsumption[hourKey].lastTimestamp && flowRate > 0) {
              const timeElapsedMinutes =
                (point.timestamp - hourlyConsumption[hourKey].lastTimestamp) / 60;
              const litersConsumed = flowRate * timeElapsedMinutes;
              hourlyConsumption[hourKey].consumption += litersConsumed;
            }

            hourlyConsumption[hourKey].lastTimestamp = point.timestamp;
          });

          const now = new Date();
          const currentHour = now.getHours();
          const hoursToDisplay = [];

          for (let i = 23; i >= 0; i--) {
            const hour = (currentHour - i + 24) % 24;
            const hourKey = `${hour.toString().padStart(2, "0")}:00`;
            const hourData = hourlyConsumption[hourKey];

            if (hourData || Object.keys(hourlyConsumption).length >= 20) {
              hoursToDisplay.push({
                hour: hourKey,
                consumption: hourData ? Number(hourData.consumption.toFixed(2)) : 0,
              });
            }
          }

          setDailyFullChartData(hoursToDisplay);
        }
      } catch (error) {
        console.error("Error obteniendo consumo de últimas 24 horas:", error);
      }
    };

    fetchLast24HoursConsumption();
    const interval = setInterval(fetchLast24HoursConsumption, 300000);
    return () => clearInterval(interval);
  }, [selectedDevice?.device_id]);

  useEffect(() => {
    const fetchFlowData = async () => {
      try {
        if (!selectedDevice?.device_id) {
          setEsp32Connected(false);
          return;
        }

        const now = new Date();

        const data = await obtenerMediciones(selectedDevice.device_id);

        if (data && data.flow_rate_lpm !== undefined) {
          setEsp32Connected(true);

          // Solo el caudal instantaneo: es el unico de estos que el dispositivo
          // envia de verdad. Los consumos los pone fetchWeeklyData desde los datos
          // agregados; ponerlos aqui los machacaba con ceros.
          setCurrentFlow(data.flow_rate_lpm);
          if (isDemoSession()) {
            setMonthConsumption(data.monthly_consumption ?? 0);
            setMonthlyComparison((prev) => ({ ...prev, currentMonth: data.monthly_consumption ?? 0 }));
          }

          const p =
            data.pressure_psi !== undefined && data.pressure_psi !== null
              ? Number(data.pressure_psi)
              : 0;
          setWaterPressure(p);
          const label = `${now.getHours().toString().padStart(2, "0")}:${now
            .getMinutes()
            .toString()
            .padStart(2, "0")}`;
          setPressureHistory((prev) => {
            const next = [...prev, { time: label, pressure: Number(p.toFixed(1)) }];
            return next.length > 48 ? next.slice(-48) : next;
          });

          // La barra de hoy la mantiene fetchWeeklyData con el consumo agregado.
          // Aqui se reescribia con data.daily_consumption, que siempre es 0, y
          // borraba el valor bueno en cuanto llegaba la siguiente lectura.
        } else {
          setEsp32Connected(false);
        }
      } catch (error) {
        setEsp32Connected(false);
        console.error("Error conectando con el dispositivo:", error.message);
      }
    };

    fetchFlowData();
    const interval = setInterval(fetchFlowData, 5000);
    return () => clearInterval(interval);
  }, [selectedDevice?.device_id, demoValveState]);

  useEffect(() => {
    const hasSeenTooltip = localStorage.getItem("hasSeenTooltip");
    if (!hasSeenTooltip) {
      /* welcome tooltip deshabilitado */
    }
  }, []);

  const getCurrentConsumption = () => {
    switch (selectedTimePeriod) {
      case "day":
        return todayConsumption;
      case "week":
        return weekConsumption;
      case "month":
        return monthConsumption;
      default:
        return todayConsumption;
    }
  };

  const currentConsumption = getCurrentConsumption();

  const effectiveDailyGoal = isDemoSession()
    ? selectedDevice?.configuration?.max_daily_usage ?? dailyGoal
    : dailyGoal;
  const daysInPeriod = selectedTimePeriod === 'week' ? 7
    : selectedTimePeriod === 'month' ? new Date().getDate() : 1;
  const consumptionStatus = isDemoSession()
    ? getDemoConsumptionStatus(currentConsumption / daysInPeriod, householdMembers, effectiveDailyGoal)
    : getConsumptionStatus(currentConsumption, householdMembers);

  const gastoHabitacionalPorLitros = calculateHousingBillByLiters(
    currentConsumption,
    "governmental"
  );
  const tarifaLabel = "Gubernamental Cat. 10";

  const flowTone = currentFlow > 50 ? "error" : currentFlow > 0 ? "info" : "success";
  const flowDelta =
    currentFlow > 50
      ? "Flujo elevado — revisa posibles fugas"
      : currentFlow > 0
        ? "Flujo detectado"
        : "Sin flujo";

  const consumptionLabel =
    selectedTimePeriod === "day"
      ? "Tu consumo de hoy"
      : selectedTimePeriod === "week"
        ? "Tu consumo de esta semana"
        : "Tu consumo de este mes";

  const costLabel =
    selectedTimePeriod === "day"
      ? "Gasto del día"
      : selectedTimePeriod === "week"
        ? "Gasto de la semana"
        : "Gasto del mes";

  const consumptionTone = consumptionStatus.status === "success" ? "success" : "error";
  const daysElapsed = new Date().getDate();

  const unitSym = getUnitSymbol(selectedUnit);

  // Ámbito de cuenta: se muestra el consolidado en lugar del detalle de un solo
  // equipo. Es una salida temprana a propósito: los efectos de arriba siguen
  // sirviendo a la vista de dispositivo sin necesidad de condicionarlos uno a uno.
  if (isAccountScope) {
    return (
      <main className="ng-home">
        <AccountOverview />
      </main>
    );
  }

  return (
    <main className="ng-home">
      <div className="ng-home__toolbar">
        <div className="ng-home__toolbar-left">
          {/* El estado sale del `last_seen` que AWS guarda para el dispositivo,
              no de que la llamada a la API haya funcionado. Antes se marcaba
              "en línea" en cuanto la petición devolvía algo, y la hora mostrada
              era la del navegador: un equipo con 110 días sin reportar aparecía
              como conectado, con "última lectura" de hace un minuto. */}
          <span className={`ng-home__connection is-${estadoDispositivo}`}>
            <span className="ng-home__connection-dot" />
            {STATUS_LABEL[estadoDispositivo]}
            <span className="ng-home__connection-time">
              {estadoDispositivo === DEVICE_STATUS.NEVER_SEEN
                ? "Aún no envía datos"
                : `Última lectura ${formatLastSeen(selectedDevice)}`}
            </span>
          </span>
        </div>
        <div className="ng-home__toolbar-right">
          <FilterPanel
            selectedTimePeriod={selectedTimePeriod}
            onTimePeriodChange={setSelectedTimePeriod}
          />
        </div>
      </div>

      <section className="ng-metric-strip">
        <MetricCard
          label="Flujo en tiempo real"
          value={Number(currentFlow).toFixed(2)}
          unit={`${unitSym}/min`}
          delta={flowDelta}
          tone={flowTone}
          accent="cyan"
          icon={<WaterDropRoundedIcon />}
        />
        <MetricCard
          label={consumptionLabel}
          value={formatValueWithUnit(currentConsumption)}
          delta={consumptionStatus.message}
          tone={consumptionTone}
          accent="accent"
          icon={
            consumptionStatus.status === "success" ? (
              <CheckCircleRoundedIcon />
            ) : (
              <TrendingUpRoundedIcon />
            )
          }
        />
        <MetricCard
          label={costLabel}
          value={`$${gastoHabitacionalPorLitros.total.toFixed(2)}`}
          unit="MXN"
          delta={`Tarifa: ${tarifaLabel} ($${gastoHabitacionalPorLitros.rate}/m³)`}
          tone={consumptionTone}
          accent="warm"
          icon={<RequestQuoteRoundedIcon />}
        />
        <WaterPressureCard
          pressure={waterPressure}
          valveClosed={isDemoSession() && selectedDevice?.valve_state === 'closed'}
          trend={isDemoSession() && selectedDevice?.valve_state === 'closed'
            ? 'Sin paso de agua'
            : esp32Connected ? "Basada en sensor o estimación por flujo" : "Sin datos en vivo"}
        />
      </section>

      <section className="ng-home__charts">
        {selectedTimePeriod === "day" && (
          <>
            <WaterAreaChart
              data={areaChartData}
              title="Flujo promedio últimas 5 horas"
              subtitle={`Promedio en ${unitSym}/min por hora`}
            />
            <div className="ng-home__pressure-trend">
              <div className="ng-home__pressure-trend-head">
                <div>
                  <h3 className="ng-bar-chart__title">Presión · últimas lecturas</h3>
                  <p className="ng-bar-chart__subtitle">Evolución de psi (cada actualización)</p>
                </div>
                <span className="chip info">tiempo real</span>
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart
                  data={pressureHistory}
                  margin={{ top: 10, right: 16, left: 0, bottom: 0 }}
                >
                  <CartesianGrid stroke={chart.grid} vertical={false} />
                  <XAxis
                    dataKey="time"
                    tickLine={false}
                    axisLine={false}
                    tick={{ fill: chart.axis, fontSize: 11 }}
                  />
                  <YAxis
                    domain={[0, 80]}
                    tickLine={false}
                    axisLine={false}
                    tick={{ fill: chart.axis, fontSize: 11 }}
                    width={32}
                  />
                  <Tooltip
                    content={<PressureTooltip color={chart.water} />}
                    cursor={{ stroke: chart.border, strokeWidth: 1 }}
                  />
                  {/* Los umbrales reales del sistema: por debajo de 30 psi la
                      presión es baja, por encima de 75 es alta. */}
                  <ReferenceLine y={30} stroke={chart.goal} strokeDasharray="3 3" />
                  <ReferenceLine y={75} stroke={chart.over} strokeDasharray="3 3" />
                  <Line
                    type="monotone"
                    dataKey="pressure"
                    stroke={chart.water}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{
                      r: 4,
                      fill: chart.surface,
                      stroke: chart.water,
                      strokeWidth: 2,
                    }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <WaterBarChart
              householdMembers={householdMembers}
              data={dailyFullChartData.map((item) => ({
                day: item.hour,
                consumo: item.consumption,
              }))}
              dailyGoal={dailyGoal}
              title="Consumo por hora últimas 24 horas"
              subtitle={isDemoSession() ? "Litros consumidos en cada hora" : undefined}
              showDailyLimit={!isDemoSession()}
            />
          </>
        )}
        {selectedTimePeriod === "week" && (
          <div className="ng-home__chart-full-span">
            <WaterBarChart
              householdMembers={householdMembers}
              data={weeklyChartData}
              dailyGoal={effectiveDailyGoal}
              title="Consumo diario últimos 7 días"
            />
          </div>
        )}
        {selectedTimePeriod === "month" && (
          <div className="ng-home__chart-full-span">
            <MonthlyComparisonChart
              currentMonth={isDemoSession() ? monthlyComparison.currentMonth : (monthlyComparison.currentMonth || monthConsumption)}
              previousMonth={monthlyComparison.previousMonth}
              twoMonthsAgo={monthlyComparison.twoMonthsAgo}
              savingsPercentage={monthlyComparison.savingsPercentage}
              savingsAmount={monthlyComparison.savingsAmount}
              title="Comparación de últimos 3 meses"
              daysElapsed={daysElapsed}
              showBillPrediction={true}
              useProjectedTrend={isDemoSession()}
            />
          </div>
        )}
      </section>
    </main>
  );
}

export default Home;
